import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { Modal } from '../../../../../../components/Modal/Modal';
import { ControlledModal } from '../../../../../../components/Modal/ControlledModal';
import { Logo, getLogos } from '../../../../../../model/logo';
import { Button } from '../../../../../../components/Button/Button';
import { t } from '../../../../../../locales/translate';
import { useRemoteData } from '../../../../../../model/data';
import { Spinner } from '../../../../../../components/Spinner/Spinner';
import { flatten } from 'lodash';
import { SearchSelect } from '../../../../../../components/Form/Select/SearchSelect';
import { Form } from '../../../../../../components/Form/Form';
import { Pagination } from '../../../../../../components/Pagination/Pagination';
import { UploadLogo } from './UploadLogo';
import { intersection } from 'lodash';
import './LogoSelector.css';
import { Icon } from '../../../../../../components/Icon/Icon';

interface Props {
  open: boolean;
  disabledLogos?: Logo[];
  onSelect?: (logos: Logo[]) => void;
  onClose?: () => void;
}

export function LogoSelector({ open, disabledLogos, onSelect, onClose }: Props): JSX.Element {
  const [logos, loading, error] = useRemoteData(getLogos, (db) => db.table('logos').all);
  const [selectedLogos, setSelectedLogos] = useState<Logo[]>([]);
  const [model, setModel] = useState({
    tags: [],
  });
  const [displayModal, setDisplayModal] = useState(false);

  function handleOnClick(): void {
    if (onSelect) {
      onSelect(selectedLogos);
    }
  }

  function selectLogo(logo: Logo): void {
    if (!selectedLogos.includes(logo)) {
      setSelectedLogos([...selectedLogos, logo]);
    }
  }

  function removeLogo(logo: Logo): void {
    setSelectedLogos(selectedLogos.filter((item) => item.id !== logo.id));
  }

  const tags = flatten(
    logos
      .filter((logo) => {
        const disabledLogosIds = disabledLogos ? disabledLogos.map((item) => item.id) : [];

        return !disabledLogosIds.includes(logo.id);
      })
      .map((i) => i.tags)
  );
  const tagOptions: Array<{ label: string; count: number }> = [];

  tags.forEach((tag) => {
    const index = tagOptions.findIndex((option) => option.label === tag);

    if (index >= 0) {
      tagOptions[index] = {
        label: tag,
        count: tagOptions[index].count + 1,
      };
    } else {
      tagOptions.push({
        label: tag,
        count: 1,
      });
    }
  });

  useEffect(() => {
    if (selectedLogos.length > 1) {
      setSelectedLogos([]);
    }
  }, [open]);

  const perPage = 12;
  const [page, setPage] = useState(0);

  const filteredLogos = [...logos]
    .reverse()
    .filter((logo) => {
      const disabledLogosIds = disabledLogos ? disabledLogos.map((item) => item.id) : [];

      return !disabledLogosIds.includes(logo.id);
    })
    .filter((logo) => {
      if (!model.tags.length) {
        return true;
      } else {
        return intersection(logo.tags, model.tags).length > 0;
      }
    });

  const paginatedLogos = filteredLogos.filter((_, index) => {
    if (index >= page * perPage && index < (page + 1) * perPage) {
      return true;
    }
    return false;
  });

  return (
    <Modal
      title="Select logo"
      className="logo-selector"
      open={open}
      size="small"
      footer={
        <>
          <Pagination
            customText="Logos per page"
            page={page}
            totalCount={filteredLogos.length}
            perPage={perPage}
            setPage={setPage}
          ></Pagination>
          <div>
            <Button
              title={t.actions.close('')}
              onClick={() => {
                if (onClose) {
                  onClose();
                }
              }}
            />
            <Button disabled={!selectedLogos.length} title="Select Logo" type="primary" onClick={handleOnClick} />
          </div>
        </>
      }
    >
      {loading && (
        <div className="logo-selector__loading">
          <div>Loading logos...</div>
          <Spinner scale="1" color="#ea3c23" />
        </div>
      )}
      {!loading && error && <div className="logo-selector__error">There was an error loading logos.</div>}
      {!loading && !error && (
        <div className="logo-selector">
          <header className="logo-selector__header">
            <Form model={model} onInput={(m) => setModel(m)}>
              <SearchSelect
                label="Search by tags"
                name="tags"
                values={tagOptions.map((item) => ({
                  id: item.label,
                  label: `${item.label} (${item.count})`,
                }))}
                icon="search--sm"
              ></SearchSelect>
            </Form>
            <Button
              title="Upload logo"
              icon="plus--sm"
              type="secondary"
              onClick={() => {
                setDisplayModal(true);
              }}
            ></Button>
          </header>
          {selectedLogos.length > 0 && (
            <div className="logo-selector__selected">
              <header>Selected ({selectedLogos.length}): </header>
              <div className="logo-selector__body">
                <ul className="logo-selector__selected-list">
                  {selectedLogos.map((item) => {
                    return (
                      <li key={item.id} className="logo-selector__selected-item" onClick={() => removeLogo(item)}>
                        <img src={item.image.url} alt={item.tags[0] || ''} />
                        <Icon name="remove-circle--sm"></Icon>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
          <ul className="logo-selector__logos">
            {paginatedLogos.map((logo) => {
              return (
                <li
                  key={logo.id}
                  className={classnames('logo-selector__logo', {
                    'logo-selector__logo--selected': selectedLogos.includes(logo),
                  })}
                  onClick={() => {
                    selectLogo(logo);
                  }}
                >
                  <div className="logo-selector__image">
                    <img src={logo.image.url} />
                  </div>
                </li>
              );
            })}
          </ul>

          <ControlledModal<{ tags?: string[] }>
            open={displayModal}
            model={{ tags: [] }}
            onClose={() => {
              setDisplayModal(false);
            }}
            title={t.actions.upload(t.model.logo._name)}
            size="small"
          >
            {(controller) => <UploadLogo controller={controller} />}
          </ControlledModal>
        </div>
      )}
    </Modal>
  );
}
